import { useTranslation } from 'react-i18next';
import { Icon, Typography, Button } from '@/components';
import { useBooleanDisplayNormalizer, useDevelopmentStatus } from '@/hooks';
import { isItemNull } from '@/features/projects/helpers';
import { customRound, displayQuarter, europeanFormatNumber } from '@/helpers';
import { useUpgradingTechnology } from '@/features/projects/hooks/use-upgrading-technology';

export type BiogasBoxProps = {
  readonly normalizedProject: any;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function BiogasBox({ normalizedProject, isEditable, onEdit }: BiogasBoxProps) {
  const { t } = useTranslation(['opportunities']);
  const { map: mapDevelopmentStatus } = useDevelopmentStatus();
  const booleanFormatter = useBooleanDisplayNormalizer();
  const { options: upgradingTechnologyOptions } = useUpgradingTechnology();
  const biogasTech = normalizedProject.technologies.find((tech: any) => tech.type === 'biogas');
  if (!biogasTech) return null;

  const item = biogasTech.details.item;
  const status = biogasTech.details.developmentStatus.status;
  if (!isEditable && !status && isItemNull(item)) return null;

  const roundedInstalledCapacity = customRound(item.capacity);
  const [statusSelected] = upgradingTechnologyOptions.filter((status) => status.value === item?.upgrading_technology);

  const fields = [
    {
      label: t('Capacity'),
      value: item.capacity ? `${europeanFormatNumber(roundedInstalledCapacity)} GWh` : null,
    },
    {
      label: t('Development Status'),
      value: mapDevelopmentStatus(status) || null,
    },
    {
      label: t('Ready to Build (RtB) Date'),
      value: item.ready_to_build ? displayQuarter(item.ready_to_build) : null,
    },
    {
      label: t('Commercial Operation Date (COD)'),
      value: item.commercial_operation_date ? displayQuarter(item.commercial_operation_date) : null,
    },
    {
      label: t('Feedstock supply radius'),
      value:
        item?.feedstock_supply_radius && item.feedstock_supply_radius !== 0
          ? `${europeanFormatNumber(item.feedstock_supply_radius, 0)} km`
          : null,
    },
    {
      label: t('Feedstock cost'),
      value:
        item?.feedstock_cost && item.feedstock_cost !== 0
          ? `${europeanFormatNumber(item.feedstock_cost, 0)} €/ton`
          : null,
    },
    {
      label: t('Upgrading Included'),
      value: booleanFormatter(item?.upgrading_included),
    },
    { label: t('Upgrading Technology'), value: statusSelected?.label ?? biogasTech?.upgrading_technology },
  ];

  const visibleFields = isEditable
    ? fields
    : fields.filter(({ value }) => value !== null && value !== undefined && value !== '');

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="biogas" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('Biogas Details & Design')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1">
        {visibleFields.map(({ label, value }) => (
          <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
            {label}:{' '}
            <span className="ml-3 text-base font-normal text-neutral-900">{value ?? (isEditable ? '—' : null)}</span>
          </Typography>
        ))}
      </div>

      {(isEditable || item.information) && (
        <Typography as="p" size="body-2" weight="semibold" relaxed>
          {t('More details')}:{' '}
          <span className="ml-3 text-base font-normal whitespace-pre-wrap text-neutral-900">
            {item.information ?? (isEditable ? '—' : null)}
          </span>
        </Typography>
      )}
    </div>
  );
}
